import React, { useEffect, useState } from "react";
import {
  Paper,
  Grid,
  Typography,
  TextField,
  FormControl,
  FormControlLabel,
  Checkbox,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Box,
  Chip,
  CircularProgress,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { ContainedButton } from "../../Common/Buttons";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  showToast,
  WEBSERVICE,
  ACCREDITEDKYCWEBSERVICE,
} from "../../CoreDataService";
import {
  ACCREDITATION_TYPES,
  CERTIFICATION_TYPES,
  ENTITY_TYPES,
  CURRENCY_OPTIONS,
  OnlyAllowedNumbers,
  allowOnlyAlphabets,
  formatCurrency,
  allowAlphanumeric,
  allowNumericOnly,
} from "./utils";

// Add to utils.js or at the top of AccreditedFinancialInfo.jsx
export const JURISDICTION_OPTIONS = [
  { value: "US-AL", label: "Alabama, USA" },
  { value: "US-AK", label: "Alaska, USA" },
  { value: "US-AZ", label: "Arizona, USA" },
  { value: "US-AR", label: "Arkansas, USA" },
  { value: "US-CA", label: "California, USA" },
  { value: "US-CO", label: "Colorado, USA" },
  { value: "US-CT", label: "Connecticut, USA" },
  { value: "US-DE", label: "Delaware, USA" },
  { value: "US-FL", label: "Florida, USA" },
  { value: "US-GA", label: "Georgia, USA" },
  { value: "US-HI", label: "Hawaii, USA" },
  { value: "US-ID", label: "Idaho, USA" },
  { value: "US-IL", label: "Illinois, USA" },
  { value: "US-IN", label: "Indiana, USA" },
  { value: "US-IA", label: "Iowa, USA" },
  { value: "US-KS", label: "Kansas, USA" },
  { value: "US-KY", label: "Kentucky, USA" },
  { value: "US-LA", label: "Louisiana, USA" },
  { value: "US-ME", label: "Maine, USA" },
  { value: "US-MD", label: "Maryland, USA" },
  { value: "US-MA", label: "Massachusetts, USA" },
  { value: "US-MI", label: "Michigan, USA" },
  { value: "US-MN", label: "Minnesota, USA" },
  { value: "US-MS", label: "Mississippi, USA" },
  { value: "US-MO", label: "Missouri, USA" },
  { value: "US-MT", label: "Montana, USA" },
  { value: "US-NE", label: "Nebraska, USA" },
  { value: "US-NV", label: "Nevada, USA" },
  { value: "US-NH", label: "New Hampshire, USA" },
  { value: "US-NJ", label: "New Jersey, USA" },
  { value: "US-NM", label: "New Mexico, USA" },
  { value: "US-NY", label: "New York, USA" },
  { value: "US-NC", label: "North Carolina, USA" },
  { value: "US-ND", label: "North Dakota, USA" },
  { value: "US-OH", label: "Ohio, USA" },
  { value: "US-OK", label: "Oklahoma, USA" },
  { value: "US-OR", label: "Oregon, USA" },
  { value: "US-PA", label: "Pennsylvania, USA" },
  { value: "US-RI", label: "Rhode Island, USA" },
  { value: "US-SC", label: "South Carolina, USA" },
  { value: "US-SD", label: "South Dakota, USA" },
  { value: "US-TN", label: "Tennessee, USA" },
  { value: "US-TX", label: "Texas, USA" },
  { value: "US-UT", label: "Utah, USA" },
  { value: "US-VT", label: "Vermont, USA" },
  { value: "US-VA", label: "Virginia, USA" },
  { value: "US-WA", label: "Washington, USA" },
  { value: "US-WV", label: "West Virginia, USA" },
  { value: "US-WI", label: "Wisconsin, USA" },
  { value: "US-WY", label: "Wyoming, USA" },
  { value: "CA", label: "Canada" },
  { value: "GB", label: "United Kingdom" },
  { value: "AU", label: "Australia" },
  { value: "DE", label: "Germany" },
  { value: "FR", label: "France" },
  { value: "JP", label: "Japan" },
  { value: "SG", label: "Singapore" },
  { value: "CH", label: "Switzerland" },
  { value: "HK", label: "Hong Kong" },
  { value: "IN", label: "India" },
];

// Helper function for entity name validation
export const allowEntityName = (e) => {
  const regex = /^[a-zA-Z0-9\s.,&\-'()]*$/;
  const key = e.key;

  // Allow control keys (backspace, delete, arrow keys, etc.)
  if (
    key === "Backspace" ||
    key === "Delete" ||
    key === "ArrowLeft" ||
    key === "ArrowRight" ||
    key === "Tab" ||
    key === "Enter"
  ) {
    return true;
  }

  // Test if the key matches the allowed pattern
  if (!regex.test(key)) {
    e.preventDefault();
    return false;
  }

  return true;
};

const AccreditedFinancialInfo = ({
  handleNext,
  handleBack,
  userDetails,
  formData,
  handleFormDataUpdate,
  eligibilityData,
  theme,
  isRejected,
}) => {
  const { btncolor } = useSelector((state) => state);
  const accreditationType = formData?.accreditationType;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [notFormedToAcquireTokens, setNotFormedToAcquireTokens] =
    useState(false);

  const CORPORATE_STRUCTURES_REQUIRING_DECLARATION = [
    "General Partnerships",
    "Limited Liability Company (by members)",
    "Limited Liability Company (sole member)",
    "Statutory Corporation",
    "LLC",
    "CORPORATION",
    "PARTNERSHIP",
  ];

  const enterpriseUser =
    userDetails?.enterpriseUser || userDetails?.enterprise_user;
  const corporateStructure =
    enterpriseUser?.corporateStructure ||
    enterpriseUser?.corporate_structure ||
    "";
  const showNotFormedDeclaration =
    accreditationType === "ENTITY" &&
    CORPORATE_STRUCTURES_REQUIRING_DECLARATION.includes(corporateStructure);

  const token = localStorage.getItem("access_token");

  // Dark mode colors
  const darkColors = {
    background: "#121212",
    paper: "#1e1e1e",
    card: "#1a1a1a",
    text: "#e0e0e0",
    textSecondary: "#aaa",
    border: "#333",
    primary: "#0d6efd",
    success: "#28a745",
    info: "#0dcaf0",
    warning: "#ffc107",
    error: "#dc3545",
  };

  // Light mode colors
  const lightColors = {
    background: "#f8f9fa",
    paper: "#ffffff",
    card: "#f8f9fa",
    text: "#212529",
    textSecondary: "#6c757d",
    border: "#dee2e6",
    primary: "#1976d2",
    success: "#2e7d32",
    info: "#0288d1",
    warning: "#ed6c02",
    error: "#d32f2f",
  };

  const colors = theme === "dark" ? darkColors : lightColors;

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isValid },
    reset,
  } = useForm({
    mode: "onChange",
    defaultValues: {
      // Income-based fields
      annualIncome: formData?.annualIncome || "",
      jointIncome: formData?.jointIncome || "",
      incomeCurrency: formData?.incomeCurrency || "USD",

      // Net worth fields
      netWorth: formData?.netWorth || "",
      netWorthCurrency: formData?.netWorthCurrency || "USD",

      // Professional certification fields
      certificationType: formData?.certificationType || "",
      certificationNumber: formData?.certificationNumber || "",
      certificationIssuer: formData?.certificationIssuer || "",
      certificationExpiry: formData?.certificationExpiry || "",

      // Entity fields
      entityName: formData?.entityName || "",
      entityType: formData?.entityType || "",
      entityRegistrationNo: formData?.entityRegistrationNo || "",
      entityJurisdiction: formData?.entityJurisdiction || "",
      entityAssets: formData?.entityAssets || "",
    },
  });

  const [annualIncomeId, setAnnualIncomeId] = useState(
    formData?.annualIncomeId || ""
  );
  const [netWorthId, setNetWorthId] = useState(formData?.netWorthId || "");

  useEffect(() => {
    if (formData) {
      reset({
        annualIncome: formData.annualIncome || "",
        jointIncome: formData.jointIncome || "",
        incomeCurrency: formData.incomeCurrency || "USD",
        netWorth: formData.netWorth || "",
        netWorthCurrency: formData.netWorthCurrency || "USD",
        certificationType: formData.certificationType || "",
        certificationNumber: formData.certificationNumber || "",
        certificationIssuer: formData.certificationIssuer || "",
        certificationExpiry: formData.certificationExpiry || "",
        entityName: formData.entityName || "",
        entityType: formData.entityType || "",
        entityRegistrationNo: formData.entityRegistrationNo || "",
        entityJurisdiction: formData.entityJurisdiction || "",
        entityAssets: formData.entityAssets || "",
      });
      setAnnualIncomeId(formData.annualIncomeId || "");
      setNetWorthId(formData.netWorthId || ""); // Add this line
    }
  }, [formData, reset]);

  const handleNetWorthChange = (event) => {
    const netWorthIdValue = event.target.value;
    setNetWorthId(netWorthIdValue);
    const netWorthValue = getIncomeFromId(netWorthIdValue);
    const netWorthRange = getIncomeRangeFromId(netWorthIdValue);

    handleFormDataUpdate({
      netWorthId: netWorthIdValue,
      netWorth: netWorthValue,
      netWorthRange: netWorthRange,
    });
  };

  // Helper functions for income ID conversion
  const getIncomeFromId = (id) => {
    const incomeRanges = {
      1: 10000,
      2: 25000,
      3: 50000,
      4: 100000,
      5: 250000,
      6: 1000000,
      7: 10000000,
    };
    return incomeRanges[id] || 0;
  };

  const getIncomeRangeFromId = (id) => {
    const incomeRanges = {
      1: { min: 0, max: 10000 },
      2: { min: 10000, max: 25000 },
      3: { min: 25000, max: 50000 },
      4: { min: 50000, max: 100000 },
      5: { min: 100000, max: 250000 },
      6: { min: 250000, max: 1000000 },
      7: { min: 1000000, max: 10000000 }, // No upper limit
    };
    return incomeRanges[id] || { min: 0, max: 0 };
  };

  const handleAnnualIncomeChange = (event) => {
    const incomeId = event.target.value;
    setAnnualIncomeId(incomeId);
    const incomeValue = getIncomeFromId(incomeId);
    const incomeRange = getIncomeRangeFromId(incomeId);

    handleFormDataUpdate({
      annualIncomeId: incomeId,
      annualIncome: incomeValue,
      annualIncomeRange: incomeRange,
    });
  };

  const onSubmit = async (data) => {
    // For rejected users, skip API call and just proceed to next step
    if (isRejected) {
      // Ensure form data is updated with current values
      const formDataWithId = {
        ...data,
        annualIncomeId: annualIncomeId,
        netWorthId: netWorthId,
      };
      handleFormDataUpdate(formDataWithId);

      // Directly proceed to document upload step
      handleNext();
      return;
    }

    setIsSubmitting(true);
    setApiError(null);

    try {
      // Prepare payload based on accreditation type
      const userId =
        userDetails?.user_id || userDetails?.userId || userDetails?.id;

      if (!userId) {
        throw new Error("User ID not found");
      }

      // Ensure annual income ID is included
      const formDataWithId = {
        ...data,
        annualIncomeId: annualIncomeId,
      };

      // Update local form data
      handleFormDataUpdate(formDataWithId);

      // Prepare API payload
      const payload = {
        userId: userId,
        accreditationType: accreditationType,
        verificationMethod: "DOCUMENT_UPLOAD",
        selfDeclaration: false,
        notFormedToAcquireTokens: showNotFormedDeclaration
          ? notFormedToAcquireTokens
          : false,
      };

      // Add type-specific fields
      switch (accreditationType) {
        case "INCOME":
          payload.annualIncome = parseFloat(data.annualIncome) || 0;
          payload.jointIncome = parseFloat(data.jointIncome) || 0;
          payload.incomeCurrency = data.incomeCurrency || "USD";
          break;

        case "NET_WORTH":
          payload.netWorth = parseFloat(data.netWorth) || 0;
          payload.netWorthCurrency = data.netWorthCurrency || "USD";
          break;

        case "PROFESSIONAL":
          payload.certificationType = data.certificationType;
          payload.certificationNumber = data.certificationNumber;
          payload.certificationIssuer = data.certificationIssuer;
          payload.certificationExpiry = data.certificationExpiry;
          break;

        case "ENTITY":
          payload.entityName = data.entityName;
          payload.entityType = data.entityType;
          payload.entityRegistrationNo = data.entityRegistrationNo;
          payload.entityJurisdiction = data.entityJurisdiction;
          payload.entityAssets = parseFloat(data.entityAssets) || 0;
          break;
      }

      // Call the API
      const response = await axios.post(
        `${ACCREDITEDKYCWEBSERVICE}accreditation/apply`,
        payload,
        {
          headers: {
            Authorization: `BEARER ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response?.data?.success) {
        // Store the application ID for document upload step
        const applicationId = response.data.data.applicationId;

        // Update form data with application ID
        handleFormDataUpdate({
          ...formDataWithId,
          applicationId: applicationId,
          applicationData: response.data.data,
        });

        // Show success message
        showToast(
          "success",
          response.data.message || "Application created successfully"
        );

        // Proceed to next step
        handleNext();
      } else {
        throw new Error(
          response?.data?.message || "Failed to create application"
        );
      }
    } catch (error) {
      console.error("API Error:", error);
      setApiError(
        error.response?.data?.message ||
          error.message ||
          "Failed to submit application. Please try again."
      );
      showToast("error", "Failed to submit application. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get eligibility info for selected type
  const getSelectedTypeEligibility = () => {
    if (!eligibilityData?.eligibleTypes || !accreditationType) return null;
    return eligibilityData.eligibleTypes.find(
      (et) => et.type === accreditationType
    );
  };

  const selectedTypeEligibility = getSelectedTypeEligibility();
  const typeInfo = ACCREDITATION_TYPES[accreditationType];
  const homeCurrencySymbol = localStorage.getItem("homeCurrencySymbol") || "$";
  const homeCurrency = localStorage.getItem("homeCurrency") || "USD";

  // Render form fields based on accreditation type
  const renderTypeSpecificFields = () => {
    switch (accreditationType) {
      case "INCOME":
        return (
          <>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: colors.textSecondary }}>
                  Annual Income *
                </InputLabel>
                <Select
                  value={annualIncomeId || ""}
                  onChange={handleAnnualIncomeChange}
                  disabled
                  label="Annual Income *"
                  sx={{
                    color: colors.text,
                    backgroundColor: colors.card,
                    "& .MuiSelect-icon": {
                      color: colors.textSecondary,
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: colors.border,
                    },
                  }}
                >
                  <MenuItem
                    value={1}
                  >{`${homeCurrencySymbol}0 - 10,000`}</MenuItem>
                  <MenuItem
                    value={2}
                  >{`${homeCurrencySymbol}10,000 - ${homeCurrencySymbol}25,000`}</MenuItem>
                  <MenuItem
                    value={3}
                  >{`${homeCurrencySymbol}25,000 - ${homeCurrencySymbol}50,000`}</MenuItem>
                  <MenuItem
                    value={4}
                  >{`${homeCurrencySymbol}50,000 - ${homeCurrencySymbol}100,000`}</MenuItem>
                  <MenuItem
                    value={5}
                  >{`${homeCurrencySymbol}100,000 - ${homeCurrencySymbol}200,000`}</MenuItem>
                  <MenuItem
                    value={6}
                  >{`${homeCurrencySymbol}200,000 - ${homeCurrencySymbol}300,000`}</MenuItem>
                  <MenuItem
                    value={8}
                  >{`${homeCurrencySymbol}300,000 - ${homeCurrencySymbol}1,000,000`}</MenuItem>
                  <MenuItem
                    value={7}
                  >{`${homeCurrencySymbol}1,000,000+`}</MenuItem>
                </Select>
              </FormControl>
              {!annualIncomeId && (
                <Typography variant="caption" color="error">
                  Annual income is required
                </Typography>
              )}
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name="jointIncome"
                control={control}
                rules={{
                  maxLength: {
                    value: 15,
                    message: "Joint income cannot exceed 15 digits",
                  },
                  pattern: {
                    value: /^[0-9]*$/,
                    message: "Only numeric values are allowed",
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Joint Income (Optional)"
                    fullWidth
                    type="text" // Changed from "number" to allow maxLength validation
                    error={!!errors.jointIncome}
                    helperText={errors.jointIncome?.message}
                    onKeyDown={OnlyAllowedNumbers}
                    inputProps={{ maxLength: 15 }}
                    sx={{
                      "& .MuiInputLabel-root": { color: colors.textSecondary },
                      "& .MuiOutlinedInput-root": {
                        color: colors.text,
                        backgroundColor: colors.card,
                        "& fieldset": {
                          borderColor: errors.jointIncome
                            ? colors.error
                            : colors.border,
                        },
                        "&:hover fieldset": { borderColor: colors.primary },
                      },
                    }}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: colors.textSecondary }}>
                  Income Currency
                </InputLabel>
                <Controller
                  name="incomeCurrency"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      value={homeCurrency} // Always set to home currency
                      label="Income Currency"
                      disabled
                      sx={{
                        color: colors.text,
                        backgroundColor: colors.card,
                        "& .MuiSelect-icon": {
                          color: colors.textSecondary,
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: colors.border,
                        },
                      }}
                    >
                      {CURRENCY_OPTIONS.map((curr) => (
                        <MenuItem key={curr.value} value={curr.value}>
                          {curr.label}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>
            </Grid>

            {formData.annualIncome && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography
                  variant="body2"
                  sx={{ color: colors.textSecondary }}
                >
                  Calculated Annual Income
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: colors.success, fontWeight: "bold" }}
                >
                  {formatCurrency(formData.annualIncome, homeCurrency)}
                </Typography>
              </Grid>
            )}

            {selectedTypeEligibility && (
              <Grid size={{ xs: 12 }}>
                <Alert
                  severity="info"
                  sx={{
                    backgroundColor:
                      colors.info + (theme === "dark" ? "20" : "10"),
                  }}
                >
                  {selectedTypeEligibility.qualificationReason && (
                    <Typography
                      variant="body2"
                      sx={{ mt: 0.5, color: colors.text }}
                    >
                      {selectedTypeEligibility.qualificationReason}
                    </Typography>
                  )}
                </Alert>
              </Grid>
            )}
          </>
        );

      case "NET_WORTH":
        return (
          <>
            {/* Net Worth Dropdown */}
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: colors.textSecondary }}>
                  Net Worth (excluding primary residence) *
                </InputLabel>
                <Select
                  value={netWorthId || ""}
                  onChange={handleNetWorthChange}
                  disabled
                  label="Net Worth (excluding primary residence) *"
                  sx={{
                    color: colors.text,
                    backgroundColor: colors.card,
                    "& .MuiSelect-icon": {
                      color: colors.textSecondary,
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: colors.border,
                    },
                  }}
                >
                  <MenuItem
                    value={1}
                  >{`${homeCurrencySymbol}0 - 10,000`}</MenuItem>
                  <MenuItem
                    value={2}
                  >{`${homeCurrencySymbol}10,000 - ${homeCurrencySymbol}25,000`}</MenuItem>
                  <MenuItem
                    value={3}
                  >{`${homeCurrencySymbol}25,000 - ${homeCurrencySymbol}50,000`}</MenuItem>
                  <MenuItem
                    value={4}
                  >{`${homeCurrencySymbol}50,000 - ${homeCurrencySymbol}100,000`}</MenuItem>
                  <MenuItem
                    value={5}
                  >{`${homeCurrencySymbol}100,000 - ${homeCurrencySymbol}250,000`}</MenuItem>
                  <MenuItem
                    value={6}
                  >{`${homeCurrencySymbol}250,000 - ${homeCurrencySymbol}1,000,000`}</MenuItem>
                  <MenuItem
                    value={7}
                  >{`${homeCurrencySymbol}1,000,000+`}</MenuItem>
                </Select>
              </FormControl>
              {!netWorthId && (
                <Typography variant="caption" color="error">
                  Net worth is required
                </Typography>
              )}
            </Grid>

            {/* Net Worth Currency */}
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: colors.textSecondary }}>
                  Net Worth Currency
                </InputLabel>
                <Controller
                  name="netWorthCurrency"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      label="Net Worth Currency"
                      disabled
                      value={homeCurrency}
                      sx={{
                        color: colors.text,
                        backgroundColor: colors.card,
                        "& .MuiSelect-icon": {
                          color: colors.textSecondary,
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: colors.border,
                        },
                      }}
                    >
                      {CURRENCY_OPTIONS.map((curr) => (
                        <MenuItem key={curr.value} value={curr.value}>
                          {curr.label}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>
            </Grid>

            {/* Display Calculated Net Worth */}
            {formData.netWorth && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography
                  variant="body2"
                  sx={{ color: colors.textSecondary }}
                >
                  Calculated Net Worth
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: colors.success, fontWeight: "bold" }}
                >
                  {formatCurrency(formData.netWorth, homeCurrency)}
                </Typography>
              </Grid>
            )}

            {/* Information Alert */}
            {selectedTypeEligibility && (
              <Grid size={{ xs: 12 }}>
                <Alert
                  severity="info"
                  sx={{
                    backgroundColor:
                      colors.info + (theme === "dark" ? "20" : "10"),
                  }}
                >
                  {selectedTypeEligibility.qualificationReason && (
                    <Typography
                      variant="body2"
                      sx={{ mt: 0.5, color: colors.text }}
                    >
                      {selectedTypeEligibility.qualificationReason}
                    </Typography>
                  )}
                </Alert>
              </Grid>
            )}
          </>
        );

      case "PROFESSIONAL":
        return (
          <>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth error={!!errors.certificationType}>
                <InputLabel sx={{ color: colors.textSecondary }}>
                  Certification Type *
                </InputLabel>
                <Controller
                  name="certificationType"
                  control={control}
                  rules={{ required: "Certification type is required" }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      label="Certification Type *"
                      sx={{
                        color: colors.text,
                        backgroundColor: colors.card,
                        "& .MuiSelect-icon": {
                          color: colors.textSecondary,
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: errors.certificationType
                            ? colors.error
                            : colors.border,
                        },
                      }}
                    >
                      {CERTIFICATION_TYPES.map((cert) => (
                        <MenuItem key={cert.value} value={cert.value}>
                          {cert.label}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                {errors.certificationType && (
                  <Typography variant="caption" color="error">
                    {errors.certificationType.message}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name="certificationNumber"
                control={control}
                rules={{
                  required: "License number is required",
                  maxLength: {
                    value: 25,
                    message: "License number cannot exceed 25 characters",
                  },
                  pattern: {
                    value: /^[a-zA-Z0-9]+$/,
                    message: "Only alphanumeric characters are allowed",
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="License Number *"
                    fullWidth
                    error={!!errors.certificationNumber}
                    helperText={errors.certificationNumber?.message}
                    onKeyDown={allowAlphanumeric}
                    inputProps={{ maxLength: 25 }}
                    sx={{
                      "& .MuiInputLabel-root": { color: colors.textSecondary },
                      "& .MuiOutlinedInput-root": {
                        color: colors.text,
                        backgroundColor: colors.card,
                        "& fieldset": {
                          borderColor: errors.certificationNumber
                            ? colors.error
                            : colors.border,
                        },
                        "&:hover fieldset": { borderColor: colors.primary },
                      },
                    }}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name="certificationIssuer"
                control={control}
                rules={{
                  required: "Issuer is required",
                  maxLength: {
                    value: 10,
                    message: "Issuer cannot exceed 10 characters",
                  },
                  pattern: {
                    value: /^[0-9]+$/,
                    message: "Only numeric values are allowed",
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Issuing Number *" // Changed from "Issuing Authority" to match numeric requirement
                    fullWidth
                    error={!!errors.certificationIssuer}
                    helperText={errors.certificationIssuer?.message}
                    onKeyDown={allowNumericOnly}
                    inputProps={{ maxLength: 10 }}
                    sx={{
                      "& .MuiInputLabel-root": { color: colors.textSecondary },
                      "& .MuiOutlinedInput-root": {
                        color: colors.text,
                        backgroundColor: colors.card,
                        "& fieldset": {
                          borderColor: errors.certificationIssuer
                            ? colors.error
                            : colors.border,
                        },
                        "&:hover fieldset": { borderColor: colors.primary },
                      },
                    }}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name="certificationExpiry"
                control={control}
                rules={{ required: "Expiry date is required" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Expiry Date *"
                    fullWidth
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    error={!!errors.certificationExpiry}
                    helperText={errors.certificationExpiry?.message}
                    sx={{
                      "& .MuiInputLabel-root": { color: colors.textSecondary },
                      "& .MuiOutlinedInput-root": {
                        color: colors.text,
                        backgroundColor: colors.card,
                        "& fieldset": {
                          borderColor: errors.certificationExpiry
                            ? colors.error
                            : colors.border,
                        },
                        "&:hover fieldset": { borderColor: colors.primary },
                      },
                    }}
                  />
                )}
              />
            </Grid>

            {selectedTypeEligibility && (
              <Grid size={{ xs: 12 }}>
                <Alert
                  severity="info"
                  sx={{
                    backgroundColor:
                      colors.info + (theme === "dark" ? "20" : "10"),
                  }}
                >
                  {selectedTypeEligibility.qualificationReason && (
                    <Typography
                      variant="body2"
                      sx={{ mt: 0.5, color: colors.text }}
                    >
                      {selectedTypeEligibility.qualificationReason}
                    </Typography>
                  )}
                </Alert>
              </Grid>
            )}
          </>
        );

      case "ENTITY":
        return (
          <>
            {/* Entity Name */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name="entityName"
                control={control}
                rules={{
                  required: "Entity name is required",
                  maxLength: {
                    value: 50,
                    message: "Entity name cannot exceed 50 characters",
                  },
                  pattern: {
                    value: /^[a-zA-Z0-9\s.,&\-'()]+$/,
                    message:
                      "Only letters, numbers, spaces, and basic punctuation (.,&-'()) are allowed",
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Entity Name *"
                    fullWidth
                    error={!!errors.entityName}
                    helperText={
                      errors.entityName?.message || "Max 50 characters"
                    }
                    onKeyDown={allowEntityName}
                    inputProps={{ maxLength: 50 }}
                    sx={{
                      "& .MuiInputLabel-root": { color: colors.textSecondary },
                      "& .MuiOutlinedInput-root": {
                        color: colors.text,
                        backgroundColor: colors.card,
                        "& fieldset": {
                          borderColor: errors.entityName
                            ? colors.error
                            : colors.border,
                        },
                        "&:hover fieldset": { borderColor: colors.primary },
                      },
                    }}
                  />
                )}
              />
            </Grid>

            {/* Entity Type */}
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth error={!!errors.entityType}>
                <InputLabel sx={{ color: colors.textSecondary }}>
                  Entity Type *
                </InputLabel>
                <Controller
                  name="entityType"
                  control={control}
                  rules={{ required: "Entity type is required" }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      label="Entity Type *"
                      sx={{
                        color: colors.text,
                        backgroundColor: colors.card,
                        "& .MuiSelect-icon": {
                          color: colors.textSecondary,
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: errors.entityType
                            ? colors.error
                            : colors.border,
                        },
                      }}
                    >
                      {ENTITY_TYPES.map((entity) => (
                        <MenuItem key={entity.value} value={entity.value}>
                          {entity.label}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                {errors.entityType && (
                  <Typography variant="caption" color="error">
                    {errors.entityType.message}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* Registration Number */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name="entityRegistrationNo"
                control={control}
                rules={{
                  maxLength: {
                    value: 50,
                    message: "Registration number cannot exceed 50 characters",
                  },
                  pattern: {
                    value: /^[a-zA-Z0-9]*$/,
                    message: "Only alphanumeric characters are allowed",
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Registration Number (Optional)"
                    fullWidth
                    error={!!errors.entityRegistrationNo}
                    helperText={
                      errors.entityRegistrationNo?.message ||
                      "Max 50 alphanumeric characters"
                    }
                    onKeyDown={allowAlphanumeric}
                    inputProps={{ maxLength: 50 }}
                    sx={{
                      "& .MuiInputLabel-root": { color: colors.textSecondary },
                      "& .MuiOutlinedInput-root": {
                        color: colors.text,
                        backgroundColor: colors.card,
                        "& fieldset": {
                          borderColor: errors.entityRegistrationNo
                            ? colors.error
                            : colors.border,
                        },
                        "&:hover fieldset": { borderColor: colors.primary },
                      },
                    }}
                  />
                )}
              />
            </Grid>

            {/* Jurisdiction - Dropdown */}
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth error={!!errors.entityJurisdiction}>
                <InputLabel sx={{ color: colors.textSecondary }}>
                  Jurisdiction *
                </InputLabel>
                <Controller
                  name="entityJurisdiction"
                  control={control}
                  rules={{ required: "Jurisdiction is required" }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      label="Jurisdiction *"
                      sx={{
                        color: colors.text,
                        backgroundColor: colors.card,
                        "& .MuiSelect-icon": {
                          color: colors.textSecondary,
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: errors.entityJurisdiction
                            ? colors.error
                            : colors.border,
                        },
                      }}
                    >
                      <MenuItem value="">
                        <em>Select Jurisdiction</em>
                      </MenuItem>
                      {JURISDICTION_OPTIONS.map((jurisdiction) => (
                        <MenuItem
                          key={jurisdiction.value}
                          value={jurisdiction.value}
                        >
                          {jurisdiction.label}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                {errors.entityJurisdiction && (
                  <Typography variant="caption" color="error">
                    {errors.entityJurisdiction.message}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* Total Assets */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name="entityAssets"
                control={control}
                rules={{
                  required: "Total assets is required",
                  min: {
                    value: 0,
                    message: "Assets must be a positive number",
                  },
                  validate: {
                    minDigits: (value) => {
                      const numStr = value.toString().replace(/[.,]/g, "");
                      return (
                        numStr.length >= 2 || "Assets must be at least 2 digits"
                      );
                    },
                    maxDigits: (value) => {
                      const numStr = value.toString().replace(/[.,]/g, "");
                      return (
                        numStr.length <= 15 || "Assets cannot exceed 15 digits"
                      );
                    },
                    validNumber: (value) => {
                      return !isNaN(value) || "Please enter a valid number";
                    },
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Total Assets *"
                    fullWidth
                    type="text"
                    error={!!errors.entityAssets}
                    helperText={
                      errors.entityAssets?.message ||
                      "Enter total assets (2-15 digits)"
                    }
                    onKeyDown={OnlyAllowedNumbers}
                    onChange={(e) => {
                      // Remove non-numeric characters except decimal point
                      const value = e.target.value.replace(/[^0-9.]/g, "");
                      // Ensure only one decimal point
                      const parts = value.split(".");
                      const formatted =
                        parts.length > 2
                          ? parts[0] + "." + parts.slice(1).join("")
                          : value;
                      // Limit to 15 digits before decimal
                      const [beforeDecimal, afterDecimal] =
                        formatted.split(".");
                      const limitedBeforeDecimal =
                        beforeDecimal?.slice(0, 15) || "";
                      const finalValue =
                        afterDecimal !== undefined
                          ? `${limitedBeforeDecimal}.${afterDecimal.slice(
                              0,
                              2
                            )}`
                          : limitedBeforeDecimal;

                      field.onChange(finalValue);
                    }}
                    inputProps={{
                      maxLength: 18, // 15 digits + 1 decimal + 2 decimal places
                      pattern: "[0-9]*",
                    }}
                    sx={{
                      "& .MuiInputLabel-root": { color: colors.textSecondary },
                      "& .MuiOutlinedInput-root": {
                        color: colors.text,
                        backgroundColor: colors.card,
                        "& fieldset": {
                          borderColor: errors.entityAssets
                            ? colors.error
                            : colors.border,
                        },
                        "&:hover fieldset": { borderColor: colors.primary },
                      },
                    }}
                  />
                )}
              />
            </Grid>

            {/* Information Alert */}
            {selectedTypeEligibility && (
              <Grid size={{ xs: 12 }}>
                <Alert
                  severity="info"
                  sx={{
                    backgroundColor:
                      colors.info + (theme === "dark" ? "20" : "10"),
                  }}
                >
                  {selectedTypeEligibility.qualificationReason && (
                    <Typography
                      variant="body2"
                      sx={{ mt: 0.5, color: colors.text }}
                    >
                      {selectedTypeEligibility.qualificationReason}
                    </Typography>
                  )}
                </Alert>
              </Grid>
            )}
          </>
        );

      default:
        return (
          <Grid size={{ xs: 12 }}>
            <Alert severity="error">
              No accreditation type selected. Please go back and select a type.
            </Alert>
          </Grid>
        );
    }
  };

  if (!accreditationType) {
    return (
      <Paper
        sx={{
          p: 3,
          backgroundColor: colors.paper,
          color: colors.text,
          borderRadius: 2,
          boxShadow:
            theme === "dark"
              ? "0 4px 6px rgba(0,0,0,0.3)"
              : "0 2px 4px rgba(0,0,0,0.1)",
        }}
      >
        <Alert severity="warning">
          Unable to determine accreditation type. Please refresh the page and
          try again.
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper
      sx={{
        p: 3,
        backgroundColor: colors.paper,
        color: colors.text,
        borderRadius: 2,
        boxShadow:
          theme === "dark"
            ? "0 4px 6px rgba(0,0,0,0.3)"
            : "0 2px 4px rgba(0,0,0,0.1)",
      }}
    >
      {/* Display selected accreditation type at top */}
      <Box
        sx={{
          mb: 3,
          p: 2,
          borderRadius: 2,
          backgroundColor: colors.primary + (theme === "dark" ? "10" : "05"),
          border: `1px solid ${colors.primary}30`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Typography variant="h6" sx={{ color: colors.text, fontWeight: 600 }}>
            {typeInfo?.icon} {typeInfo?.label || accreditationType}
          </Typography>
          {eligibilityData?.recommendedType === accreditationType && (
            <Chip
              label="Recommended"
              size="small"
              sx={{
                ml: 2,
                height: 22,
                backgroundColor: colors.success,
                color: "#fff",
                fontWeight: "bold",
              }}
            />
          )}
        </Box>
        {/* <Typography variant="body2" sx={{ color: colors.textSecondary }}>
          {typeInfo?.description}
        </Typography> */}
        {eligibilityData?.recommendationReason && (
          <Typography
            variant="body2"
            sx={{ mt: 1, color: colors.primary, fontWeight: "bold" }}
          >
            ✓ {eligibilityData.recommendationReason}
          </Typography>
        )}
      </Box>

      <Typography
        variant="h6"
        gutterBottom
        sx={{ color: colors.text, fontWeight: 600 }}
      >
        Provide Financial Details
      </Typography>
      {/* <Typography
        variant="body2"
        sx={{
          mb: 5,  // Changed from mb: 3 to mb: 5 for more spacing
          color: colors.textSecondary,
          wordWrap: 'break-word',
          overflowWrap: 'break-word',
          whiteSpace: 'normal'
        }}
      >
        Please provide the required information for {typeInfo?.shortLabel} verification.
      </Typography> */}

      {apiError && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            backgroundColor: colors.error + (theme === "dark" ? "20" : "10"),
          }}
        >
          {apiError}
        </Alert>
      )}

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Grid container spacing={3}>
          {renderTypeSpecificFields()}
        </Grid>

        {showNotFormedDeclaration && (
          <Box sx={{ mt: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={notFormedToAcquireTokens}
                  onChange={(e) =>
                    setNotFormedToAcquireTokens(e.target.checked)
                  }
                  sx={{
                    color: colors.primary,
                    "&.Mui-checked": {
                      color: colors.primary,
                    },
                    alignSelf: "flex-start",
                    mt: "4px",
                  }}
                />
              }
              label={
                <Typography
                  variant="body2"
                  sx={{ color: colors.text, lineHeight: 1.5 }}
                >
                  I confirm that this entity was not formed specifically for the
                  purpose of acquiring the securities or tokens being offered.
                </Typography>
              }
              sx={{ alignItems: "flex-start", mx: 0, mt: 1 }}
            />
          </Box>
        )}

        <div
          style={{
            marginTop: "24px",
            display: "flex",
            alignItems: "center",
            gap: "16px",
          }}
        >
          <ContainedButton
            type="submit"
            variant="contained"
            sx={{
              borderRadius: "1.25rem",
              padding: "10px 25px",
              backgroundColor: colors.primary,
              "&:hover": {
                backgroundColor: theme === "dark" ? "#0b5ed7" : "#1565c0",
              },
              "&:disabled": {
                backgroundColor: colors.textSecondary,
                color: colors.text,
              },
            }}
            disabled={
              !isValid ||
              (accreditationType === "INCOME" && !annualIncomeId) ||
              (accreditationType === "NET_WORTH" && !netWorthId) ||
              (showNotFormedDeclaration && !notFormedToAcquireTokens) ||
              isSubmitting
            }
          >
            {isSubmitting ? (
              <>
                <CircularProgress size={20} sx={{ color: "#fff", mr: 1 }} />
                Submitting...
              </>
            ) : (
              "Next"
            )}
          </ContainedButton>

          {isSubmitting && (
            <Typography variant="body2" sx={{ color: colors.textSecondary }}>
              Creating application and preparing document upload...
            </Typography>
          )}
        </div>
      </form>
    </Paper>
  );
};

export default AccreditedFinancialInfo;
